import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";

const filters = [
  { id: "all", label: "All Work" },
  { id: "painting", label: "Painting" },
  { id: "wood", label: "Wood Refinishing" },
  { id: "cleaning", label: "Floor Cleaning" },
  { id: "installation", label: "Installation" },
];

const projects = [
  {
    id: 101,
    cat: "painting",
    title: "Wooden Cabinets — Before",
    duration: "Before",
    img: "/work/cabinets-before.jpg",
    alt: "Unfinished wooden cabinets and bench before painting",
  },
  {
    id: 102,
    cat: "painting",
    title: "Wooden Cabinets — Spraying First Coat",
    duration: "In progress",
    img: "/work/cabinets-spray.jpg",
    alt: "Xtra Clean crew member spraying the first coat of paint on wooden cabinets with everything masked",
  },
  {
    id: 103,
    cat: "painting",
    title: "Wooden Cabinets — After",
    duration: "After",
    img: "/work/cabinets-after.jpg",
    alt: "Finished painted wooden cabinets with butcher block counter and bench",
  },
  {
    id: 3,
    cat: "painting",
    title: "Bathtub Refinish — Masked & Prepped",
    duration: "Careful prep",
    img: "/work/tub-prep.jpg",
    alt: "Bathtub masked and prepped with tape and sheeting for refinishing",
  },
  {
    id: 4,
    cat: "painting",
    title: "Worn Tub — Before",
    duration: "Before",
    img: "/work/tub-before.jpg",
    alt: "Worn, stained bathtub before refinishing",
  },
  {
    id: 5,
    cat: "painting",
    title: "Bathtub Refinish — After",
    duration: "After",
    img: "/work/tub-after.jpg",
    alt: "Glossy white bathtub after professional refinishing",
  },
  {
    id: 6,
    cat: "wood",
    title: "Hardwood Floor Staining",
    duration: "Stain & seal",
    img: "/work/floor-staining.jpg",
    alt: "Glossy finish being applied to a hardwood floor",
  },
  {
    id: 9,
    cat: "painting",
    title: "Wall Prep & Dust-Free Sanding",
    duration: "Behind the scenes",
    img: "/work/drywall-sanding.jpg",
    alt: "Crew member dust-free sanding walls before painting",
  },
  {
    id: 7,
    cat: "cleaning",
    title: "Commercial Carpet Extraction",
    duration: "Commercial space",
    img: "/work/carpet-cleaning.jpg",
    alt: "Professional carpet extraction cleaning in a commercial space",
  },
  {
    id: 8,
    cat: "cleaning",
    title: "Upholstery & Sofa Cleaning",
    duration: "Fabric care",
    img: "/work/upholstery-cleaning.jpg",
    alt: "Deep cleaning a fabric sofa with an upholstery tool",
  },
  {
    id: 10,
    cat: "painting",
    title: "Exterior Door Spray Finish",
    duration: "Masked & protected",
    img: "/work/door-spray.jpg",
    alt: "Spraying a smooth factory finish on an exterior door with everything masked off",
  },
  {
    id: 11,
    cat: "painting",
    title: "Wall Texture & Spray Rig",
    duration: "Full remodel",
    img: "/work/texture-spray.jpg",
    alt: "Professional texture spray rig in a room being resurfaced",
  },
  {
    id: 104,
    cat: "painting",
    title: "Watch: Spraying Ceiling Texture",
    duration: "Video",
    video: "/work/texture-spray.mp4",
    img: "/work/texture-spray-poster.jpg",
    alt: "Xtra Clean crew member spraying ceiling texture with the room fully masked off",
  },
  {
    id: 12,
    cat: "cleaning",
    title: "Saltillo Tile — Cleaned & Sealed",
    duration: "After",
    img: "/work/saltillo-sealed.jpg",
    alt: "Glossy sealed Saltillo tile floor reflecting the windows",
  },
  {
    id: 13,
    cat: "cleaning",
    title: "Saltillo Tile Deep Scrub",
    duration: "In progress",
    img: "/work/terracotta-scrub.jpg",
    alt: "Deep scrubbing and extracting a Saltillo tile floor",
  },
  {
    id: 14,
    cat: "cleaning",
    title: "Grout Restoration — Before",
    duration: "Before",
    img: "/work/grout-after.jpg",
    alt: "Darker stained grout lines before restoration",
  },
  {
    id: 15,
    cat: "cleaning",
    title: "Grout Restoration — After",
    duration: "After",
    img: "/work/grout-renewal.jpg",
    alt: "Restored grout lines after professional cleaning",
  },
  {
    id: 16,
    cat: "installation",
    title: "Staircase Carpet Install",
    duration: "After",
    img: "/work/carpet-stairs.jpg",
    alt: "Freshly installed plush carpet on a wooden staircase",
  },
  {
    id: 17,
    cat: "installation",
    title: "Upstairs Landing Carpet",
    duration: "After",
    img: "/work/carpet-landing.jpg",
    alt: "New carpet installed on an upstairs landing and steps",
  },
  {
    id: 18,
    cat: "installation",
    title: "Bedroom Carpet Install",
    duration: "After",
    img: "/work/carpet-bedroom.jpg",
    alt: "Bedroom with freshly installed wall-to-wall carpet",
  },
  {
    id: 19,
    cat: "installation",
    title: "Whole-Home Carpet Finish",
    duration: "After",
    img: "/work/carpet-bedroom2.jpg",
    alt: "Second bedroom finished with new wall-to-wall carpet",
  },
];

const ease = [0.16, 1, 0.3, 1];

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const visible =
    filter === "all" ? projects : projects.filter((p) => p.cat === filter);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-white py-20 lg:py-32"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease }}
          className="font-mono-x text-xs uppercase tracking-[0.3em] text-red-600 font-semibold mb-4"
        >
          Recent Transformations
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-xl mb-10"
        >
          Proof, not promises.
        </motion.h2>

        <div className="flex flex-wrap gap-3 mb-10">
          {filters.map((f) => (
            <button
              key={f.id}
              data-testid={`gallery-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-5 py-2.5 font-mono-x text-xs font-semibold uppercase tracking-wider border-2 transition-all duration-200 active:scale-[0.98] ${
                filter === f.id
                  ? "bg-red-600 border-red-600 text-white"
                  : "border-zinc-300 text-zinc-600 hover:border-zinc-900 hover:text-zinc-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.button
                layout
                key={p.id}
                data-testid={`gallery-card-${p.id}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease }}
                onClick={() => setActive(p)}
                className="group relative text-left overflow-hidden border border-zinc-200 aspect-[4/3] max-h-[80vh]"
              >
                {p.video ? (
                  <>
                    <video
                      data-testid={`gallery-video-${p.id}`}
                      src={p.video}
                      poster={p.img}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-red-600 text-white font-mono-x text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                      <Play size={10} className="fill-white" /> Watch
                    </span>
                  </>
                ) : (
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 inset-x-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <p className="font-display font-bold text-white text-lg tracking-tight">
                    {p.title}
                  </p>
                  <p className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-red-400 mt-1">
                    {p.duration}
                  </p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-zinc-950/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={() => setActive(null)}
          >
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {active.video ? (
                <video
                  data-testid="gallery-lightbox-video"
                  src={active.video}
                  poster={active.img}
                  autoPlay
                  loop
                  playsInline
                  controls
                  className="w-full max-h-[75vh] object-contain bg-zinc-950 border-4 border-white"
                />
              ) : (
                <img
                  src={active.img}
                  alt={active.alt}
                  className="w-full max-h-[75vh] object-cover border-4 border-white"
                />
              )}
              <figcaption className="mt-4 flex items-center justify-between">
                <div>
                  <p className="font-display font-bold text-white text-xl">
                    {active.title}
                  </p>
                  <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-red-400 mt-1">
                    {active.duration}
                  </p>
                </div>
                <button
                  data-testid="gallery-lightbox-close"
                  onClick={() => setActive(null)}
                  className="p-3 bg-red-600 hover:bg-red-700 text-white transition-colors"
                  aria-label="Close preview"
                >
                  <X size={20} />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
